import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { VenueDto } from "@core/models/dto/FacilityBookingConfigurationsDto";
import { BookingConfigurationService } from "@core/services/booking-configuration.service";
import { Option } from "@core/models/Option";

@Injectable({ providedIn: 'root' })
export class VisionService {
  constructor(){
    effect(() => {
      if(this.facilityServiceTypeId() > 0){
        this.getCountryByType$(this.facilityServiceTypeId()).subscribe(
          res => {
            this.venues.set(res);
          });
      }
    }, {allowSignalWrites: true})
  }

  facilityServiceTypeId = signal(-1);
  private bookingConfigurationService = inject(BookingConfigurationService);
  private bookingConfigurationVision = toSignal(this.bookingConfigurationService.getFacilityBookingConfigurations());
  venues = signal<Array<VenueDto>>([]);

  facilityServiceTypes = computed(() => {
    return this.bookingConfigurationVision()?.facilityServiceTypes.map(ivt => (
      new Option(ivt.id, ivt.name)
    ))
  })

  facilityInterviewTypes = computed(() => {
    return this.bookingConfigurationVision()?.interviewTypes
    .filter(ivt => !ivt.interVuOnly)
    .map(ivt => (new Option(ivt.id, ivt.name)))
  })

  facilityCountries = computed(() => {
    return this.venues().map(country => (
      new Option(country.id, country.name, {'europeanUnion': country.europeanUnion})
    ))
  })

  languageOptions = computed(() => {
    return this.bookingConfigurationVision()?.multiLanguageAudioOptions.map(l => (
      new Option(l.id, l.name)
    ))
  })

  getCountryByType$ = (id:number) =>  this.bookingConfigurationService.getFacilityCountriesByServiceType(id);

  getCitiesByCountry(countryId: number) {
    return this.venues()
               .find(c => c.id == countryId)
               ?.cities
               .map(city => new Option(city.id, city.name));
  }

  getFacilitiesByCountryAndCity(countryId: number, cityId: number) {
    return  this.venues()
                .find(c => c.id == countryId)
                ?.cities
                .find(c => c.id == cityId)
                ?.facilities
                .map(fac => new Option(fac.name, fac.name, {'isPremium': fac.isPremium}));
  }

  getCountryById(countryId: number): string | undefined {
    return this.venues().find(country => country.id == countryId)?.name;
  }

  getCityById(cityId: number, countryId: number): string | undefined {
    return this.venues().find(c => c.id == countryId)?.cities.find(c => c.id == cityId)?.name;
  }

  getVenues() {
    return this.getCountryByType$(this.facilityServiceTypeId());
  }

  getAdditionalInfoByServiceType(serviceTypeId: Number): string{
    switch (serviceTypeId) {
      case 1: return '360 camera placed at the center of the table to automatically capture the active speaker (available at select venues)';
      case 2: return 'Stationary camera';
      case 3: return 'Moving camera with onsite technician';
      case 4: return 'Portable solution for non-facility locations with internet access';
    }
    return '';
  }
}
